import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Subscription billing actions — Upgrade to Pro (Task Group 8) and Manage
 * Subscription (Task Group 9). Both endpoints already exist and are reused
 * as-is (FR-4.2/FR-5.2); this service just wraps them for the web app.
 *
 * Neither call needs an explicit `returnPath` — the Hub's own defaults
 * already point at this ticket's routes: checkout-session's default is
 * `/billing/subscription/return` (`buildReturnUrls`'s WEB_DEFAULT_PATHS),
 * portal-link's default is `/billing` (both confirmed directly in
 * user-payments-controller.ts). Passing them explicitly would just
 * duplicate what the Hub already assumes.
 *
 * Per billing-payments § 1.0 (Q0): the web NEVER imports `@stripe/stripe-js`.
 * All Stripe interaction is via hosted-page redirect, matching
 * `MapAnalysisCheckoutService`'s existing pattern.
 */
export interface CheckoutSessionResult {
  /** Stripe-hosted Checkout URL. Assign to window.location to redirect. */
  url: string;
  sessionId: string;
}

export interface PortalLinkResult {
  /** Stripe-hosted Customer Portal URL. Assign to window.location to redirect. */
  url: string;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionBillingService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  async createCheckoutSession(): Promise<CheckoutSessionResult> {
    const response = await firstValueFrom(
      this.http.post<{ success: boolean; data: CheckoutSessionResult; error?: string }>(
        `${this.apiUrl}/payments/subscriptions/checkout-session`,
        {},
      ),
    );
    if (!response?.success || !response.data?.url) {
      throw new Error(response?.error || 'Failed to create checkout session');
    }
    return response.data;
  }

  async createPortalLink(): Promise<PortalLinkResult> {
    const response = await firstValueFrom(
      this.http.post<{ success: boolean; data: PortalLinkResult; error?: string }>(
        `${this.apiUrl}/payments/subscriptions/portal-link`,
        {},
      ),
    );
    if (!response?.success || !response.data?.url) {
      throw new Error(response?.error || 'Failed to create portal link');
    }
    return response.data;
  }

  /**
   * Redirects the browser to a Stripe-hosted URL (Checkout or Portal).
   * `url` comes from the Hub's response body, and this is the single choke
   * point every redirect in this service goes through, so it's the cheapest
   * place to guard against a malformed/compromised response turning into an
   * open-redirect, phishing, or `javascript:`-URL vector: enforces `https:`
   * AND that the host is Stripe's own domain (this service only ever redirects
   * to Stripe-hosted Checkout/Portal pages — an arbitrary-but-https domain is
   * still the wrong destination) (Copilot catches on PR #108).
   */
  redirect(url: string): void {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      throw new Error('Received an invalid redirect URL from the server.');
    }
    const isStripeHost = parsed.hostname === 'stripe.com' || parsed.hostname.endsWith('.stripe.com');
    if (parsed.protocol !== 'https:' || !isStripeHost) {
      throw new Error('Received an invalid redirect URL from the server.');
    }
    window.location.assign(parsed.href);
  }
}
